import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import type { AppDefinition } from '@/types/app';
// Pure, and the phone's: the page's own lists and words, read out of its source.
import { readCopy, readSeedCollections } from '@/webapp/content';
// Pure: which collections the app keeps on the server for its owner to fill (`S.list`), and the
// fields it reads off them — the same derivation a publish declares them with.
import { publishedSchemas } from '@/webapp/schemasFromSource';
import { displayAddress } from '@/webapp/siteAddress';
import { SITE_STATUS_LABEL } from '@/utils/websiteSheet';
import { accountsIn, addSignInRequest, reachOf, signInSummary } from '../../../state/people';
import { saveManagedSource, type ManageSaved } from '../../../state/manageSave';
import type { SiteCheck } from '../../../state/siteStatus';
import { Button, EmptyState } from '../../../ui';
import ContentSection from './ContentSection';
import LiveSection from './LiveSection';
import { uploadSitePhoto } from './sitePhotos';
import TextSection from './TextSection';

type Section = 'content' | 'live' | 'text';

/** Said while a chat edit is on its way: it saves over the source these sections rewrite. */
export const CHAT_EDIT_ON_ITS_WAY =
  'Chat is changing this app right now. You can save here once that change has landed.';

/**
 * Looking after a site once it exists — the web's Manage, the phone's Manage tab
 * (src/webapp/WebAppAdminPanel.tsx) in the same words.
 *
 * The owner could change their site from a sentence in Chat and nowhere else: to fix one price
 * or one date they had to ask for a regeneration, which risks everything else, and the rows a
 * site keeps on the server (this week's tee times) had no editor on the web at all.
 *
 *  - Content: the lists written into the page. Saved and republished, as chat's edits are.
 *  - Live: rows kept on the server. Live for every visitor on save, no republish.
 *  - Text: the page's wording. Saved and republished.
 *
 * With who can sign in, and the way to People and Replies, which on the web are pages of their
 * own; and the address, with Open.
 */
export default function ManagePanel({
  projectId,
  ownerId,
  definition,
  site,
  chatBusy,
  onBusyChange,
  onAskInChat,
}: {
  projectId: string;
  ownerId: string;
  definition: AppDefinition;
  /** Whether the page is up, as the server said to the editor. */
  site: SiteCheck;
  /** A chat edit is on its way. */
  chatBusy: boolean;
  /** Told while a save here is publishing, so nothing else publishes over it. */
  onBusyChange?: (busy: boolean) => void;
  /** Opens Chat with `text` written in, for the owner to read and send. */
  onAskInChat: (text: string) => void;
}) {
  const source = definition.source ?? '';
  /*
   * Only the lists written into the page (`const COURSES = [ … ]`). An S.store seed is SAMPLE
   * data, copied into each visitor's browser the first time they open the site and never again,
   * so editing it changes nothing for anyone who already has — the phone's rule.
   */
  const collections = useMemo(() => readSeedCollections(source).filter((c) => c.kind !== 'store'), [source]);
  /*
   * The collections a publish declares `ownerWrite`: every `S.list` the page reads, with the
   * fields it reads off each. A name the page ALSO submits into or saves per person is declared
   * that way instead, and the owner cannot write it as a list, so it is not offered.
   */
  const managed = useMemo(
    () =>
      publishedSchemas(source)
        .filter((s) => s.ownership === 'ownerWrite')
        .map((s) => ({ name: s.name, fields: s.fields.map((f) => f.name) })),
    [source],
  );
  const copy = useMemo(() => readCopy(source), [source]);

  const SECTIONS: { id: Section; label: string; hint: string }[] = [
    ...(collections.length > 0 ? [{ id: 'content' as const, label: 'Content', hint: 'Edit the app content' }] : []),
    ...(managed.length > 0 ? [{ id: 'live' as const, label: 'Live', hint: 'Edit rows that change for everyone straight away' }] : []),
    ...(copy.length > 0 ? [{ id: 'text' as const, label: 'Text', hint: 'Edit the wording on the page' }] : []),
  ];
  // The section picked while it still exists — a chat edit can take a list or a phrase away —
  // else the first there is.
  const [chosen, setChosen] = useState<Section | null>(null);
  const section = SECTIONS.find((s) => s.id === chosen)?.id ?? SECTIONS[0]?.id ?? null;
  const only = SECTIONS.length === 1 ? SECTIONS[0] : null;

  const [notice, setNotice] = useState<ManageSaved | null>(null);
  async function saveSource(next: string, what: string): Promise<void> {
    setNotice(null);
    onBusyChange?.(true);
    try {
      // Made FROM the source these sections were drawn with: if the stored app has moved on,
      // the save refuses rather than lay this text over the other change.
      setNotice(await saveManagedSource(projectId, ownerId, source, next, what));
    } finally {
      onBusyChange?.(false);
    }
  }

  /** A photo for a Live row, made one of the site's photos (sitePhotos.ts). Busy like a save. */
  async function uploadPhoto(file: File) {
    onBusyChange?.(true);
    try {
      return await uploadSitePhoto({ projectId, ownerId, file });
    } finally {
      onBusyChange?.(false);
    }
  }

  // Whether the site has sign-in, in the phone's sentence; who can open it once the server said.
  const accounts = accountsIn(source);
  // Only a site with a sign-up form has anyone to email — the phone's test for its Campaign tab.
  const collectsEmail = source.includes('S.subscribe(');
  const reach = reachOf(site);
  const hostedAppId = definition.hostedAppId;
  const signIn = signInSummary(
    accounts,
    reach,
    hostedAppId ? 'Everyone who has signed in is listed under People using this site.' : '',
  );

  // The address as SiteLine reads it: the server's for a page it serves, the record's until then.
  const known = site.kind === 'known' ? site : null;
  const offline = known?.status === 'offline';
  const url = offline ? definition.webUrl : (known?.siteUrl ?? definition.webUrl);
  const address = url ? displayAddress(url) : '';

  return (
    <div className="manage-panel">
      {url && (
        <section className="card clay-card people-status" aria-label="Address">
          <p className="type-caption">{known ? `Your website · ${SITE_STATUS_LABEL[known.status]}` : 'Your website'}</p>
          <p className="type-body publish-url">
            {address}{' '}
            {/* A page the server says is down is not a link: it would open "This site isn't available". */}
            {!offline && (
              <a href={url} target="_blank" rel="noreferrer" aria-label={`Open your website ${address} in a new tab`}>
                Open <span aria-hidden="true">↗</span>
              </a>
            )}
          </p>
        </section>
      )}

      <section className="card clay-card people-status" aria-label="People">
        <p className="type-heading">{signIn.headline}</p>
        <p className="type-body">{signIn.detail}</p>
        {accounts === 'none' && (
          <>
            <Button
              title="Add sign-in"
              variant="secondary"
              onPress={() => onAskInChat(addSignInRequest(definition.plan, source))}
            />
            <p className="type-caption">Opens Chat with the request written for you to send.</p>
          </>
        )}
        <p className="type-body editor-links">
          {/* People is read from the server under the catalogue id: a site never published has nobody. */}
          {hostedAppId && (
            <>
              <Link to={`/users/${hostedAppId}`} state={{ accounts, reach }}>People using this site</Link>
              {' · '}
            </>
          )}
          <Link to={`/submissions/${projectId}`}>See replies people sent</Link>
          {/* The phone's Campaign, for a site with a mailing list; sent under the catalogue id. */}
          {hostedAppId && collectsEmail && (
            <>
              {' · '}
              <Link to={`/broadcast/${hostedAppId}`}>Email the mailing list</Link>
            </>
          )}
        </p>
      </section>

      {notice && (
        <p role={notice.status === 'saved_not_live' ? 'alert' : 'status'} className="type-body">
          {notice.message}
        </p>
      )}

      {SECTIONS.length > 1 && (
        <div className="filter-row" role="tablist" aria-label="What to manage">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              id={`manage-tab-${s.id}`}
              aria-selected={section === s.id}
              aria-controls="manage-section"
              title={s.hint}
              className={section === s.id ? 'chip-button is-selected' : 'chip-button'}
              onClick={() => setChosen(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}
      {/* A lone section gets no tab bar, so it gets its name instead. */}
      {only && <h2 className="type-title">{only.label}</h2>}

      {chatBusy && section !== 'live' && section !== null && <p role="status" className="type-caption">{CHAT_EDIT_ON_ITS_WAY}</p>}

      <div
        id="manage-section"
        role={SECTIONS.length > 1 ? 'tabpanel' : undefined}
        aria-labelledby={SECTIONS.length > 1 && section ? `manage-tab-${section}` : undefined}
      >
        {section === 'content' ? (
          <ContentSection collections={collections} source={source} locked={chatBusy} onSaveSource={saveSource} />
        ) : section === 'live' ? (
          <LiveSection
            hostedAppId={hostedAppId}
            collections={managed}
            source={source}
            assets={definition.assets ?? []}
            // Adding a photo republishes the page, so not while a chat edit is publishing its own.
            onUploadPhoto={chatBusy ? undefined : uploadPhoto}
          />
        ) : section === 'text' ? (
          <TextSection copy={copy} source={source} locked={chatBusy} onSaveSource={saveSource} />
        ) : (
          <EmptyState
            icon="assets"
            title="Nothing to edit here"
            body="This app has no fixed lists or wording to change here. Ask for any change in Chat."
          />
        )}
      </div>
    </div>
  );
}
